import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["button"];
  static values = {
    title: String,
    text: String,
    fileUrl: String,
    filename: String,
    busy: Boolean,
  };

  connect() {
    if (typeof navigator.canShare !== "function") {
      this.element.hidden = true;
    }
  }

  async share(event) {
    event.preventDefault();
    if (this.busyValue) return;
    this.busyValue = true;
    try {
      const file = await this.#fetchFile();
      const data = {
        title: this.titleValue,
        text: this.textValue,
        files: [file],
      };
      if (!navigator.canShare(data)) {
        // Rare branch: connect() already covers unsupported browsers. Hits here
        // only when canShare rejects THIS specific payload — iOS over plain
        // HTTP, an unsupported MIME type, or a file over the platform size cap.
        // Hide to prevent a second click doing nothing.
        console.warn("Share unsupported for this file:", data);
        this.element.hidden = true;
        return;
      }
      await navigator.share(data);
    } catch (err) {
      if (err.name !== "AbortError") console.error("Share failed:", err);
    } finally {
      this.busyValue = false;
    }
  }

  busyValueChanged(busy) {
    if (!this.hasButtonTarget) return;
    this.buttonTarget.loading = busy;
    this.buttonTarget.disabled = busy;
  }

  async #fetchFile() {
    // Bound the fetch so a stalled blob doesn't leave the button disabled forever.
    const abort = new AbortController();
    const timeoutId = setTimeout(() => abort.abort(), 30_000);
    try {
      const res = await fetch(this.fileUrlValue, { signal: abort.signal });
      if (!res.ok)
        throw new Error(`Fetch failed: ${res.status} for ${this.fileUrlValue}`);
      const blob = await res.blob();
      return new File([blob], this.filenameValue, { type: blob.type });
    } catch (err) {
      // Distinguish our own timeout from navigator.share()'s user-cancel
      // AbortError — the outer catch silently swallows the latter.
      if (err.name === "AbortError") {
        throw new Error(`Fetch timed out after 30s: ${this.fileUrlValue}`);
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  }
}
